import { useEffect, useState, useCallback } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import debounce from "lodash.debounce";
import { fetchAccount, fetchAccounts, getAccountTypes, getPostalCodeByKey } from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";

const limit = 20;

const Accounts = () => {
    const [searchValue, setSearchValue] = useState('');
    const [decouncedSearchValue, setDecouncedSearchValue] = useState('');
    const [showMoreColumns, setShowMoreColumns] = useState(false);

    const { ref, inView } = useInView();

    const { data, error, fetchNextPage, hasNextPage, isLoading, isFetching, isFetchingNextPage, status } = useInfiniteQuery({
        queryKey: ["accounts", limit, decouncedSearchValue],
        queryFn: async ({ pageParam }) => {
            let params = pageParam || {};

            if (decouncedSearchValue) {
                params.condition = 'OR'
                params.accountHccId = decouncedSearchValue;
                params.accountName = decouncedSearchValue;
            }

            return await fetchAccounts({ ...params, limit, accountLevel: undefined });
        },

        select: (data) => {
            return {
                count: data.pages?.[0]?.count,
                rows: data.pages.map((page) => page.rows).flatMap((x) => x)
            };
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            const hasMore = lastPage?.rows?.length === limit;

            if (!hasMore) return undefined;
            return {
                offset: pages.reduce((accum, page) => {
                    return accum + page.rows.length;
                }, 0),
            };
        },
        staleTime: 0,
        enabled: true,
    });

    const { data: accountTypes } = useQuery({
        queryKey: ['getAccountTypes'],
        queryFn: () => getAccountTypes(),
        select: (data) => data?.rows.filter(row => row.accountTypeCode.includes('AT'))
    });

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event.target.value?.trim());
        }, 500),
        []
    );

    let columns = ['accountHccId', 'accountName', 'taxId', 'accountEffectiveDateKey', 'versionEffDateKey'];

    if (showMoreColumns) {
        columns = [
            ...columns,
            'accountLevel',
            'accountStatus',
            'renewalInterval',
            'canHaveSubscr',
            'billed',
            'billingCycleName',
            'generalCorrSameAsParent',
            'otherCorrSameAsParent'
        ];
    }

    const onDetailHandler = async (body) => {
        const {
            accountKey,
            accountHccId,
            accountName,
            accountCorrAddressKey,
            accountEffectiveDateKey,
            versionEffDateKey,
            taxId,
            accountLevel,
            accountStatus,
            accountTypeKey,
            renewalInterval,
            canHaveSubscr,
            billed,
            billingCycleName,
            generalCorrSameAsParent,
            otherCorrSameAsParent
        } = body;

        const [account, address] = await Promise.all([
            fetchAccount({ accountIdentifier: { hccIdentificationNumber: accountHccId, referenceIdentificationNumber: accountKey, name: accountName } }),
            getPostalCodeByKey({ postalAddressKey: accountCorrAddressKey })
        ]);

        const { topAccountHccIdentifier, parentAccountHccIdentifier } = account;
        const { addressLine, addressLine2, addressLine3, cityName, stateCode, zipCode } = address?.rows.at(0);

        const accountType = accountTypeKey && accountTypes
            ? accountTypes.find(type => type.accountTypeKey === accountTypeKey)?.accountTypeDesc
            : undefined;

        return {
            accountHccId,
            accountName,
            taxId,
            accountEffectiveDateKey,
            versionEffDateKey,
            accountLevel,
            accountStatus,
            accountType,
            topAccountHccId: topAccountHccIdentifier ?? accountHccId,
            parentAccountHccId: parentAccountHccIdentifier ?? accountHccId,
            generalCorrSameAsParent,
            otherCorrSameAsParent,
            addressLine,
            addressLine2,
            addressLine3,
            cityName,
            stateCode,
            zipCode,
            renewalInterval,
            canHaveSubscr,
            billed,
            billingCycleName,
        };
    }

    return (
        <div className={"container "}>
            <p className="title">Accounts ( {data?.count || 0} )</p>
            <p className="sub-title">List of accounts from HRP</p>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1rem 1rem 0rem', }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap', }}>
                    <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"search"}
                        placeholder="search..."
                        autoComplete="off"
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {
                        setDecouncedSearchValue("");
                        setSearchValue("");
                    }}
                >
                    Clear filters
                </p>

                <p style={{ cursor: "pointer", color: "var(--green)" }} onClick={() => setShowMoreColumns(!showMoreColumns)}>
                    {showMoreColumns ? "Less Columns" : "More Columns"}
                </p>
            </div>

            {status === "loading" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className='danger'>Error: {error.message}</span>
            ) : (
                <>
                    <SharedTable
                        type={"account"}
                        columns={columns}
                        items={data?.rows}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        onDetailHandler={onDetailHandler}
                        ref={ref}
                    />
                </>
            )}
        </div>
    );
};

export default Accounts;
